import axiosInstance from '../axios/axiosInstance';
import type { PaginatedResponse } from './types';

// Defense-in-depth: backend aggregations now $ifNull the subscribed field
// (TELV1-220), but the admin dashboard has historically rendered "Free"
// whenever the value was falsy — including for legacy orgs whose schema
// predates the subscription object. Coercing at the API boundary means the
// entire UI can treat `subscribed` as a guaranteed boolean without every
// component having to repeat the defensive check.
const normalizeOrganization = <T extends { subscribed?: unknown }>(org: T): T => ({
  ...org,
  subscribed: org.subscribed === true,
});

export interface Organization {
  _id: string;
  name: string;
  owner: {
    _id: string;
    email: string;
    name?: string;
  };
  subscribed: boolean;
  deletedAt?: string;
  createdAt: string;
  updatedAt: string;
  memberCount?: number;
  workspaceCount?: number;
  projectCount?: number;
}

export interface OrganizationMember {
  _id: string;
  email: string;
  name?: string;
  last_name?: string;
  role: string;
  status: 'active' | 'pending' | 'deactivated';
  joinedAt?: string;
  invitedAt?: string;
  lastActiveAt?: string;
}

export interface OrganizationWorkspace {
  _id: string;
  name: string;
  organizationId: string;
  createdAt: string;
  projectCount?: number;
  memberCount?: number;
}

export interface OrganizationFilters {
  page?: number;
  limit?: number;
  search?: string;
  includeDeleted?: boolean;
  subscribed?: boolean;
  dateRange?: 'all' | '7d' | '30d' | '90d';
  sortBy?: 'name' | 'email' | 'createdAt' | 'memberCount' | 'workspaceCount';
  sortOrder?: 'asc' | 'desc';
}

export interface OrganizationWorkspaceFilters {
  page?: number;
  limit?: number;
  search?: string;
}

export interface InviteUserPayload {
  email: string;
  role: string;
  workspaceIds?: string[];
  workspaceRole?: 'CAN_EDIT' | 'CAN_VIEW';
}

export interface UpdateSubscriptionPayload {
  subscribed: boolean;
}

export interface CreateOrganizationPayload {
  name: string;
  ownerEmail: string;
  createDefaultWorkspace?: boolean;
}

export const organizationsApi = {
  /**
   * Create a new organization
   */
  create: async (payload: CreateOrganizationPayload): Promise<Organization> => {
    const { data } = await axiosInstance.post<Organization>('/organizations', payload);
    return normalizeOrganization(data);
  },

  /**
   * Get all organizations with filters and pagination
   */
  getAll: async (params: OrganizationFilters): Promise<PaginatedResponse<Organization>> => {
    const { data } = await axiosInstance.get<PaginatedResponse<Organization>>(
      '/organizations',
      { params }
    );
    return { ...data, data: data.data.map(normalizeOrganization) };
  },

  /**
   * Get a single organization by ID
   */
  getById: async (id: string): Promise<Organization> => {
    const { data } = await axiosInstance.get<Organization>(`/organizations/${id}`);
    return normalizeOrganization(data);
  },

  /**
   * Update organization subscription status
   */
  updateSubscription: async (
    id: string,
    payload: UpdateSubscriptionPayload
  ): Promise<Organization> => {
    const { data } = await axiosInstance.patch<Organization>(
      `/organizations/${id}/subscription`,
      payload
    );
    return normalizeOrganization(data);
  },

  /**
   * Restore a soft-deleted organization
   */
  restore: async (id: string): Promise<Organization> => {
    const { data } = await axiosInstance.patch<Organization>(`/organizations/${id}/restore`);
    return normalizeOrganization(data);
  },

  /**
   * Get all workspaces for an organization
   */
  getWorkspaces: async (
    id: string,
    params?: OrganizationWorkspaceFilters
  ): Promise<PaginatedResponse<OrganizationWorkspace>> => {
    const { data } = await axiosInstance.get<PaginatedResponse<OrganizationWorkspace>>(
      `/organizations/${id}/workspaces`,
      { params }
    );
    return data;
  },

  /**
   * Get all members of an organization
   */
  getMembers: async (
    id: string,
    params?: { page?: number; limit?: number }
  ): Promise<PaginatedResponse<OrganizationMember>> => {
    const { data } = await axiosInstance.get<PaginatedResponse<OrganizationMember>>(
      `/organizations/${id}/members`,
      { params }
    );
    return data;
  },

  /**
   * Invite a user to an organization
   */
  inviteUser: async (id: string, payload: InviteUserPayload): Promise<void> => {
    await axiosInstance.post(`/organizations/${id}/invite`, payload);
  },

  /**
   * Resend invitation to a user
   */
  resendInvite: async (id: string, email: string): Promise<void> => {
    await axiosInstance.post(`/organizations/${id}/resend-invite`, { email });
  },

  /**
   * Remove a member from an organization
   */
  removeMember: async (id: string, userId: string): Promise<void> => {
    await axiosInstance.delete(`/organizations/${id}/members/${userId}`);
  },

  /**
   * Update a member's role in an organization
   */
  updateMemberRole: async (id: string, userId: string, role: string): Promise<void> => {
    await axiosInstance.patch(`/organizations/${id}/members/${userId}/role`, { role });
  },
};
